import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  CalendarDay,
  InsightsBaseProps,
  NEUTRAL_TONE,
  WEEKDAY_ABBREVIATION_KEYS,
  formatLocalDate,
  formatShortDate,
  getDayScore,
  getDayTone,
  getMondayFirstWeekdayIndex,
  hasCalendarScore,
} from './insightsCalendarUtils';

type InsightsCalendarProps = InsightsBaseProps & {
  startDate?: Date;
};

// Grelha de calendario mensal partilhada entre Lite e Pro (fonte visual unica).
const InsightsCalendar: React.FC<InsightsCalendarProps> = ({
  days,
  startDate,
  titleKey = 'insightsLiteMonthlyTitle',
  descriptionKey = 'insightsLiteMonthlySubtitle',
  children,
}) => {
  const { i18n, t } = useTranslation();

  const cells = useMemo(() => {
    if (!startDate) {
      return days.map((day) => ({ day }));
    }

    const leading = Array.from({ length: getMondayFirstWeekdayIndex(startDate) }, () => ({ day: null as CalendarDay | null }));
    return [...leading, ...days.map((day) => ({ day: day as CalendarDay | null }))];
  }, [days, startDate]);

  return (
    <>
    <Card className="border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center text-slate-800">
          <CalendarDays className="mr-2 h-5 w-5 text-violet-600" />
          {t(titleKey)}
        </CardTitle>
        <CardDescription>{t(descriptionKey)}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-emerald-300 bg-emerald-100" />{t('insightsLiteStrongDay')}</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-sky-300 bg-sky-100" />{t('insightsLiteBalancedDay')}</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-rose-300 bg-rose-100" />{t('insightsLiteCautionDay')}</span>
        </div>
        <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-500">
          {WEEKDAY_ABBREVIATION_KEYS.map((key) => (
            <span key={key}>{t(key)}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map(({ day }, index) => {
            if (!day) {
              return <div key={`empty-${index}`} className="min-h-[68px]" />;
            }

            const isToday = day.date ? formatLocalDate(day.date) === formatLocalDate(new Date()) : false;

            if (!hasCalendarScore(day)) {
              return (
                <div
                  key={day.date?.toISOString() ?? index}
                  className={`flex min-h-[68px] flex-col rounded-md border p-1.5 ${NEUTRAL_TONE.border} ${NEUTRAL_TONE.bg} ${
                    isToday ? 'ring-2 ring-inset ring-action-600' : ''
                  }`}
                  title={formatShortDate(day.date, i18n.language)}
                >
                  <span className="text-xs font-bold text-slate-500">{day.date?.getDate() ?? '-'}</span>
                  <span className={`mt-auto text-sm font-bold ${NEUTRAL_TONE.text}`}>-</span>
                </div>
              );
            }

            const score = getDayScore(day);
            const tone = getDayTone(score);

            return (
              <div
                key={day.date?.toISOString() ?? index}
                className={`flex min-h-[68px] flex-col rounded-md border p-1.5 transition-colors ${tone.border} ${tone.bg} ${
                  isToday ? 'ring-2 ring-inset ring-action-600' : ''
                }`}
                title={`${formatShortDate(day.date, i18n.language)} ${score}%${
                  day.bestProfileKey ? ` · ${t('profile_' + day.bestProfileKey)}` : ''
                }`}
              >
                <span className="text-xs font-bold text-slate-900">{day.date?.getDate() ?? '-'}</span>
                <span className={`mt-auto text-sm font-bold ${tone.text}`}>{score}%</span>
                <span className="mt-0.5 hidden truncate text-[11px] text-slate-600 sm:block">
                  {day.bestProfileKey ? t('profile_' + day.bestProfileKey) : '-'}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
    {children}
    </>
  );
};

export default InsightsCalendar;
